import { useState, useEffect } from 'react'
import { activityApi } from '../lib/api'
import { formatDate } from '../lib/format'
import { useAuth } from '../hooks/useAuth'
import { Badge, Spinner } from './ui'

// Icons for different activity types
const ACTIVITY_ICONS = {
  document_uploaded: '📄',
  document_reviewed: '👀',
  document_approved: '✅',
  document_rejected: '❌',
  permission_granted: '🔑',
  agent_assigned: '🤝',
  agent_draft_uploaded: '📝',
  agent_finalized: '🎯',
  transaction_added: '💰',
  task_completed: '✅',
  status_changed: '🔄',
  document_edited: '✏️',
  document_deleted: '🗑️',
  staff_invited: '👤',
  staff_removed: '🚫',
}

const ACTIVITY_COLORS = {
  document_uploaded: 'border-emerald-200 bg-emerald-50',
  document_reviewed: 'border-blue-200 bg-blue-50',
  document_approved: 'border-emerald-200 bg-emerald-50',
  document_rejected: 'border-rose-200 bg-rose-50',
  permission_granted: 'border-amber-200 bg-amber-50',
  agent_assigned: 'border-purple-200 bg-purple-50',
  agent_draft_uploaded: 'border-violet-200 bg-violet-50',
  agent_finalized: 'border-emerald-200 bg-emerald-50',
  transaction_added: 'border-teal-200 bg-teal-50',
  task_completed: 'border-emerald-200 bg-emerald-50',
  status_changed: 'border-neutral-200 bg-neutral-50',
}

export default function TeamActivityFeed({ companyId, documentId = null, limit = 30 }) {
  const [activities, setActivities] = useState([])
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState(false)
  const { user } = useAuth()

  useEffect(() => {
    if (!companyId) return
    loadActivities()
  }, [companyId, documentId])

  const loadActivities = async () => {
    setLoading(true)
    const { data, error } = await activityApi.getCompanyActivity(companyId, limit)
    if (!error && data) {
      const filtered = documentId 
        ? data.filter(a => a.entity_id === documentId)
        : data
      setActivities(filtered)
    }
    setLoading(false)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner size="sm" />
        <span className="ml-2 text-sm text-muted">Loading activity...</span>
      </div>
    )
  }

  if (activities.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-muted">No activity to show yet.</p>
        <p className="text-xs text-neutral-400 mt-1">Activities will appear here as team members work.</p>
      </div>
    )
  }

  const displayActivities = expanded ? activities : activities.slice(0, 10)

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-ink flex items-center gap-2">
          <span className="text-lg">📋</span> Team Activity
          <Badge variant="default" size="sm">{activities.length}</Badge>
        </h4>
        {activities.length > 10 && (
          <button 
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-brand hover:text-brand-dark transition-colors"
          >
            {expanded ? 'Show less' : `Show all (${activities.length})`}
          </button>
        )}
      </div>
      
      <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
        {displayActivities.map((activity) => {
          const icon = ACTIVITY_ICONS[activity.action_type] || '📌'
          const colorClass = ACTIVITY_COLORS[activity.action_type] || 'border-neutral-200 bg-neutral-50'
          const isMine = activity.actor_id === user?.id

          return (
            <div 
              key={activity.id}
              className={`flex items-start gap-3 p-3 border rounded-lg transition-colors ${colorClass} ${
                isMine ? 'ring-1 ring-brand/20' : ''
              }`}
            >
              <div className="w-8 h-8 bg-white border border-neutral-200 rounded-full flex items-center justify-center shrink-0 text-sm">
                {icon}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-sm font-medium ${isMine ? 'text-brand' : 'text-ink'}`}>
                    {activity.actor_name || 'Unknown'}
                  </span>
                  <Badge variant="default" size="sm">
                    {activity.actor_role || 'User'}
                  </Badge>
                  {isMine && (
                    <Badge variant="primary" size="sm">You</Badge>
                  )}
                </div>
                
                <p className="text-sm text-neutral-600 mt-0.5">
                  {activity.action_details}
                </p>
                
                <p className="text-[10px] text-neutral-400 mt-1 font-mono">
                  {formatDate(activity.created_at)}
                </p>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}